import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const sharedData: Pick<LocDef, 'feature' | 'htmlAllowed' | 'shared' | 'portal'> = {
	feature: HubFeature.MergeCandidates,
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
};

export type SPCreateMergeLocs =
	| 'SPCreateMergePageTitle'
	| 'SPCreateMergeTabTitle'
	| 'SPCreateMergePrimaryTitle'
	| 'SPCreateMergeSecondaryTitle'
	| 'SPCreateMergePrimaryButton'
	| 'SPCreateMergeSecondaryButton'
	| 'SPCreateMergeNoSelection'
	| 'SPCreateMergeSubmitAction'
	| 'SPCreateMergeSwapAction'
	| 'SPCreateMergeInvalidSelection'
	| 'SPCreateMergeAssignmentInstructions'
	| 'SPCreateMergeSearchInstructions';

export const spCreateMergeLocs: LocDefs<SPCreateMergeLocs> = {
	SPCreateMergePageTitle: {
		defaultValue: 'Create Candidate Merge',
		description: 'Page title for creating a candidate merge',
		legacyName: 'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.PageTitle',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Page Title',
		displayType: 'Title',
		...sharedData,
	},
	SPCreateMergeTabTitle: {
		defaultValue: 'Create Merge',
		description: 'Tab title.',
		legacyName: 'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Title.Tab',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Tab Title',
		displayType: 'Title',
		...sharedData,
	},
	SPCreateMergePrimaryTitle: {
		defaultValue: 'Winner',
		description: 'Title listed above selected winning candidate',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Title.WinningCandidateTitle',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Winning Candidate Title',
		displayType: 'Title',
		...sharedData,
	},
	SPCreateMergeSecondaryTitle: {
		defaultValue: 'Loser',
		description: 'Title listed above selected losing candidate',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Title.LosingCandidateTitle',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Losing Candidate Title',
		displayType: 'Title',
		...sharedData,
	},
	SPCreateMergePrimaryButton: {
		defaultValue: 'Winner',
		description: 'Text on button to select winning candidate from search results.',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Button.WinningCandidateAction',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Select Winning Candidate',
		displayType: 'Button',
		...sharedData,
	},
	SPCreateMergeSecondaryButton: {
		defaultValue: 'Loser',
		description: 'Text on button to select losing candidate from search results.',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Button.LosingCandidateAction',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Select Losing Candidate',
		displayType: 'Button',
		...sharedData,
	},
	SPCreateMergeNoSelection: {
		defaultValue: 'No candidate selected',
		description: 'Message shown when a candidate has not been selected yet.',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Content.NoCandidateSelected',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|No Candidate Selected',
		displayType: 'Content',
		...sharedData,
	},
	SPCreateMergeSubmitAction: {
		defaultValue: 'Merge Candidates',
		description: 'Text on button to proceed to merge candidates',
		legacyName: 'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Button.MergeCandidates',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Merge Candidates',
		displayType: 'Button',
		...sharedData,
	},
	SPCreateMergeSwapAction: {
		defaultValue: 'Swap',
		description: 'Text on button to swap selected candidates',
		legacyName: 'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Button.Swap',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Swap Candidates',
		displayType: 'Button',
		...sharedData,
	},
	SPCreateMergeInvalidSelection: {
		defaultValue: 'Cannot select the same candidate',
		description:
			'Message displayed when the same candidate is selected for both winner and loser.',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Duplicate Candidates',
		displayType: 'Content',
		...sharedData,
	},
	SPCreateMergeAssignmentInstructions: {
		defaultValue: 'Assign candidates to be merged below',
		description: 'Instructions for assigning candidates displayed after searching.',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Assign Instructions',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Content.AssignInstructions',
		displayType: 'Content',
		feature: HubFeature.MergeCandidates,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
	SPCreateMergeSearchInstructions: {
		defaultValue: 'Use the search above to find candidates to assign to the merge',
		description: 'Instructions displayed when no candidate results are available from search.',
		displayPath: 'Tools|Merges|Merge Queue|Create Merge|Search Instructions',
		displayType: 'Content',
		legacyName:
			'MergeCandidates/Tools.Merges.MergeQueue.CreateMerge.Content.SearchInstructions',
		feature: HubFeature.MergeCandidates,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
};

export type SPCreateMergeLocKeys = LocMap<SPCreateMergeLocs>;

export const spCreateMergeLocKeys = MapLocKeys<SPCreateMergeLocs>(spCreateMergeLocs);
